import { act, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useSearchParams } from "react-router-dom";
import { RootState, AppDispatch } from "../../store";
import { fetchList } from "../../store/slices/listSlice";
import EmptyListingScreen from "../emptyScreens/EmptyListingScreen";
import TableHeader from "./TableHeader";
import TableTopBar from "./TableTopBar";
import { TableDetailsItem } from "./tableFunctions";
import Paginate from "./Paginate";
import useTranslation from "../CustomHooks/useTranslation";
import { breadcrumbsContent } from "../breadcrumbs/BreadcrumbsContent";
import { setUpfrontPercentage } from "../../store/slices/upfrontPercentageSlice";
import { setPreviousUrl } from "../../store/slices/geolocationSlice";
import { setActiveList } from "../../store/slices/activeListSlice";
import AccessDeniedScreen from "../emptyScreens/AccessDenied";
import { DynamicFilterConfig } from "../DynamicComponent/DynamicFilterPanel";
import dayjs from "dayjs";

interface MainTableProps {
  staticText: {
    emptyScreenText: {
      heading1: string;
      heading2?: string;
    };
  };
  mainEndPoint: string;
  TableDetails: TableDetailsItem;
  handleActions: (id: string, key: string, text?: string) => void;
  params?: { [key: string]: string };
  searchPlaceholder?: string;
  filters: boolean;
  search?: boolean;
  exporting: boolean;
  serviceNow?: boolean;
  displayTotalRecords?: boolean;
  noListing?: boolean;
  notfoundRoute?: { [key: string]: { [key: string]: string } };
  title?: string;
  placeholderClassName?: string;
  hasListPrivilege?: boolean;
  hasActivateDeactivateMDPrivilege?: boolean;
  hasViewMDPrivilege?: boolean;
  hasEditMDPrivilege?: boolean;
  mnoList?: boolean;
  fetchListTrigger?: boolean;
  amountList?: boolean;
  handleActiveList?: (key: string) => void;
  // privileges?: any;
  TabChildren?: React.ReactNode;
  stickyStyleTableHeader?: string;
  statusNotFilter?: boolean;
  staticEndPoint?: string;
  dynamicFilter?: { [key: string]: { mapItem: string[]; type: string } };
  customHeight?: boolean;
  filterHeight?: string;
  modificationRequest?: boolean;
  dynamicFilterConfig?: DynamicFilterConfig;
  enableExcel?: boolean;
}

export default function MainTable({
  staticText,
  mainEndPoint,
  TableDetails,
  handleActions,
  noListing,
  params = {},
  searchPlaceholder,
  TabChildren,
  filters,
  search = true,
  serviceNow,
  exporting,
  displayTotalRecords = false,
  notfoundRoute = {},
  title,
  placeholderClassName,
  hasListPrivilege = true,
  hasActivateDeactivateMDPrivilege = true,
  hasViewMDPrivilege = true,
  hasEditMDPrivilege = true,
  mnoList,
  fetchListTrigger,
  stickyStyleTableHeader,
  amountList = false,
  statusNotFilter,
  staticEndPoint,
  handleActiveList,
  customHeight,
  filterHeight,
  dynamicFilter,
  modificationRequest,
  dynamicFilterConfig,
  enableExcel
}: MainTableProps) {
  const dispatch = useDispatch<AppDispatch>();
  const listState = useSelector((state: RootState) => state.list);
  let { activeList } = useSelector((state: RootState) => state.activeList);
  const { loading, error, data, upfrontServiceFee, recordCount } = listState;
  const { translate } = useTranslation(breadcrumbsContent);
  const filersWithEmpty = {
    heading1: translate("No results found"),
    heading2: translate("Try adjusting your filter's or search criteria"),
    img: "search_empty_screen_img",
  };
  const [searchParams, setSearchParams] = useSearchParams(params);
  const [disableTab, setDisableTab] = useState(false);
  const [showFilter, setShowFilter] = useState(true);
  
  // Generate unique request ID for tracking to prevent stale responses
  const generateRequestId = () => `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  const getList = () => {
    setDisableTab(true);
    const params = Object.fromEntries(searchParams.entries());
    setShowFilter(Object.keys(params).length > 1 ? true : false);
    const searchParamsString = new URLSearchParams(params).toString();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const url = `${mainEndPoint}${modificationRequest ? '?modification_details=true' : ''}${staticEndPoint ? `?${staticEndPoint}&` : modificationRequest ? '&' : "?"
      }${searchParamsString}&client_timezone=${timezone}`;
    
    // Generate a unique request ID to track this specific request
    // This prevents stale responses from overwriting data when navigating between tabs
    const requestId = generateRequestId();
    
    dispatch(fetchList({
      url,
      requestId,
    }));
    setDisableTab(false);
  };

  useEffect(() => {
    const params = Object.fromEntries(searchParams.entries());
    if (params.payment_status === "false" && amountList) {
      dispatch(setActiveList("Pending"));
    } else {
      dispatch(setActiveList("Purchased"));
    }
  }, [mainEndPoint]);

  useEffect(() => {
    dispatch(setUpfrontPercentage(upfrontServiceFee?.toString() || ""));
  }, [upfrontServiceFee]);

  useEffect(() => {
    getList();
  }, [searchParams, mainEndPoint, fetchListTrigger, activeList, modificationRequest]);

  useEffect(() => {
    const paymentStatus = searchParams.get("payment_status");

    if (
      (activeList === "Pending" && paymentStatus === "false") ||
      (activeList === "Purchased" && !paymentStatus)
    ) {
      // Keep all current search params as is
      return;
    } else if (activeList === "Pending") {
      // Reset to expected 'Pending' params
      setSearchParams(
        new URLSearchParams({ payment_status: "false", page_number: "1" })
      );
    } else if (activeList === "Purchased") {
      // Clear all if there are unwanted params
      setSearchParams(new URLSearchParams({ page_number: "1" }));
    }
  }, [activeList]);
  const handleSearch = (searchTerm: string) => {
    handleFilter({ search: searchTerm });
  };
  const handleFilter = (filters: { [key: string]: any } | null) => {
    if (!dynamicFilterConfig || Object.keys(dynamicFilterConfig).length === 0) {
      // Apply second filter logic when dynamicFilterConfig is undefined or empty
      setSearchParams((prev) => {
        const updatedParams = new URLSearchParams(prev);
        updatedParams.set("page_number", "1");
        Object.keys(filters || {}).forEach((key) => {
          const value = filters?.[key];
          if (value !== null && value !== undefined && value !== "") {
            if (Array.isArray(value)) {
              updatedParams.set(key, value.join(","));
            } else {
              updatedParams.set(key, value.toString());
            }
          } else {
            updatedParams.delete(key);
          }
        });
        if (activeList === "Pending" && amountList) {
          updatedParams.set("payment_status", "false");
        }
        return updatedParams;
      });
    } else {
      // Apply first filter logic when dynamicFilterConfig exists
      setSearchParams((prev) => {
        const updatedParams = new URLSearchParams(prev);
        updatedParams.set("page_number", "1");
        // If filters is null, clear all filter keys
        if (filters === null && dynamicFilterConfig) {
          Object.values(dynamicFilterConfig).forEach((conf) => {
            if (conf.type === "date" && typeof conf.searchKey === "object") {
              updatedParams.delete(conf.searchKey.start);
              updatedParams.delete(conf.searchKey.end);
            } else if (typeof conf.searchKey === "string") {
              updatedParams.delete(conf.searchKey);
            }
          });
          return updatedParams;
        }
        // ...existing logic for non-null filters...
        if (filters) {
          Object.keys(filters).forEach((key) => {
            let value = filters[key];
            // Handle date range objects
            if (
              value &&
              typeof value === "object" &&
              ("start" in value || "end" in value)
            ) {
              const { start, end } = value as { start?: dayjs.Dayjs | null; end?: dayjs.Dayjs | null };
              if (start && typeof start.format === "function") {
                updatedParams.set(`${key}_start`, start.format("YYYY-MM-DD"));
              } else {
                updatedParams.delete(`${key}_start`);
              }
              if (end && typeof end.format === "function") {
                updatedParams.set(`${key}_end`, end.format("YYYY-MM-DD"));
              } else {
                updatedParams.delete(`${key}_end`);
              }
              // Don't set the main key for the range object
              updatedParams.delete(key);
            }
            // Handle single dayjs object
            else if (value && typeof value === "object" && typeof (value as any).format === "function") {
              value = (value as dayjs.Dayjs).format("YYYY-MM-DD");
              updatedParams.set(key, value.toString());
            }
            // Handle primitives
            else if (value !== null && value !== "") {
              updatedParams.set(key, value.toString());
            } else {
              updatedParams.delete(key);
            }
          });
        }
        return updatedParams;
      });
    }
  };

  const handleSort = (key: string) => {
    setSearchParams((prev) => {
      const updatedParams = new URLSearchParams(prev);
      const currentSort = updatedParams.get("sort");
      let newSort;

      if (currentSort) {
        const sortArray = currentSort.split(",");
        const existingSort = sortArray.find((s) => s.startsWith(key));

        if (existingSort) {
          const [field, order] = existingSort.split("+");
          const newOrder = order === "asc" ? "desc" : "asc";
          newSort = [
            `${field}+${newOrder}`,
            ...sortArray.filter((s) => !s.startsWith(key)),
          ].join(",");
        } else {
          newSort = [`${key}+asc`, ...sortArray].join(",");
        }
      } else {
        newSort = `${key}+asc`;
      }

      updatedParams.set("page_number", "1");
      updatedParams.set("sort", newSort);
      if (activeList === "Pending") {
        updatedParams.set("payment_status", "false");
      }
      return updatedParams;
    });
  };

  const exortSearchParams = new URLSearchParams(
    Array.from(searchParams.entries()).filter(([key]) => key !== "page_number"  && key !== "cursor")
  );

  const onlyPaymentStatusOrEmpty = () => {
    const entries = Array.from(searchParams.entries());
    return (
      entries.length === 0 ||
      entries.every(
        ([key]) => key === "payment_status" || key === "page_number"
      )
    );
  };

  return (
    <div
      className={`mt-1.5 bg-white ${mainEndPoint.includes('requests') ? 'h-maintableheight3' : title === 'List of all Pending Amount' ? 'h-pendingCashInAmountHeight' : 'h-maintableheight'} rounded-lg`}
    >
      {TabChildren ? TabChildren : null}
      {amountList && (
        <div className="flex px-4 pt-6 pb-4 justify-start gap-4 items-center">
          <span
            className={`${activeList === "Purchased"
              ? "text-p_primary border-b-2 border-p_primary hover:border-p_dark_active hover:text-p_dark_active"
              : "text-secondary_text border-b-2 border-[#A4A9AE]"
              } font-medium p-1 cursor-pointer`}
            onClick={() => {
              loading ? null : handleActiveList?.("Purchased");
            }}
          >
            Purchased Amount
          </span>
          <span
            id="Pending_Amount"
            className={`${activeList === "Pending"
              ? "text-p_primary border-b-2 border-p_primary hover:border-p_dark_active hover:text-p_dark_active"
              : "text-secondary_text border-b-2 border-[#A4A9AE]"
              } font-medium p-1 cursor-pointer`}
            onClick={() => {
              loading ? null : handleActiveList?.("Pending");
            }}
          >
            Pending Amount
          </span>
        </div>
      )}
      {!noListing && hasListPrivilege && (
        <TableTopBar
          onSearch={handleSearch}
          onFilter={handleFilter}
          // searchValue={searchParams.get("search")}
          loading={loading || !data}
          searchParams={searchParams}
          searchPlaceholder={searchPlaceholder}
          filters={filters}
          search={search}
          serviceNow={serviceNow}
          exporting={exporting}
          endpoint={`${mainEndPoint}${amountList || statusNotFilter || mainEndPoint.includes('mnos/service-fee') ? `/` : ""
            }export${staticEndPoint ? `?${staticEndPoint}&` : "?"
            }${modificationRequest ? 'modification_details=true' : ""}${(modificationRequest && exortSearchParams.toString()) ? "&" : ""}${exortSearchParams.toString()}${activeList === "Pending" ? "&payment_status=false" : ""
            }`}
          disableExport={data && data.length === 0}
          disableSearchAndFilter={
            recordCount === 0 && onlyPaymentStatusOrEmpty()
          }
          recordCount={recordCount}
          placeholderClassName={placeholderClassName}
          mainEndPoint={mainEndPoint}
          mnoList={mnoList}
          amountList={amountList}
          showFilter={showFilter}
          activeList={activeList} 
          statusNotFilter={statusNotFilter}
          dynamicFilter={dynamicFilter}
          dynamicFilterConfig={dynamicFilterConfig}
          filterHeight={filterHeight}
          enableExcel={enableExcel}
        />
      )}
      {loading ? (
        <div className="p-6">
          <TableHeader
            listData={data}
            TableDetails={TableDetails}
            handleActions={handleActions}
            loading={loading}
            handleSort={handleSort}
            amountList={amountList || TabChildren ? true : false || mainEndPoint.includes('cash-in')}
            TabChildren={customHeight ? true : false}
            stickyStyleTableHeader={stickyStyleTableHeader}
            mainEndPoint={mainEndPoint}
          />
        </div>
      ) : !hasListPrivilege ? (
        <AccessDeniedScreen />
      ) : !noListing && data && data.length > 0 ? (
        <div className="p-6">
          <TableHeader
            listData={data}
            TableDetails={TableDetails}
            handleActions={handleActions}
            loading={loading}
            handleSort={handleSort}
            searchParams={searchParams}
            getList={getList}
            hasActivateDeactivateMDPrivilege={hasActivateDeactivateMDPrivilege}
            hasViewMDPrivilege={hasViewMDPrivilege}
            hasEditMDPrivilege={hasEditMDPrivilege}
            mnoList={mnoList}
            amountList={amountList || TabChildren ? true : false || mainEndPoint.includes('cash-in')}
            TabChildren={customHeight ? true : false}
            stickyStyleTableHeader={stickyStyleTableHeader}
            mainEndPoint={mainEndPoint}
          />
          {recordCount && Number(recordCount) > 10 && (
            <div
              className={`${amountList || TabChildren ? "mt-4" : customHeight ? "mt-2" : ""
                }`}
            >
              <Paginate
                totalPages={Math.ceil((recordCount || 0) / 10)}
                currentPage={Number(searchParams.get("page_number")) || 1}
                onPageChange={(page) =>
                  setSearchParams({
                    ...Object.fromEntries(searchParams),
                    page_number: String(page),
                  })
                }
                loading={loading}
                displayTotalRecords={displayTotalRecords}
                title={title}
              />
            </div>
          )}
        </div>
      ) : (
        <div
          className={`flex items-center justify-center ${amountList || TabChildren
            ? "h-emptyscreenheight_2"
            : customHeight
              ? "h-emptyscreenheight_3"
              : "h-emptyscreenheight"
            }`}
        >
          <EmptyListingScreen
            emptyScreenText={
              onlyPaymentStatusOrEmpty()
                ? staticText.emptyScreenText
                : filersWithEmpty
            }
            imageHight={customHeight ? "h-[225px]" : ""}
          />
        </div>
      )}
    </div>
  );
}
